import { useMemo, useState } from "react";
import { useSelector } from "store";
import { scrollTop } from "utility";

import {
  createReceiptData,
  parseReceiptOrderData,
} from "../../utils/createReceiptData";

import type { OrderData } from "../../types/OrderData";
import type { ProductItem } from "../../types/ProductItem";
import type { ReceiptData } from "containers/account/receipt/AccountReceipt";

type UseReceiptStateInput = {
  orderData: OrderData | null;
  orderList: ProductItem[];
};

type UseReceiptStateOutput = {
  isDisplayReceipt: boolean;
  /** 領収書を出せるかどうか。注文未取得、または注文データが不正なときはfalse */
  isReceiptAvailable: boolean;
  receiptData: ReceiptData | null;
  handleShowReceipt: () => void;
  handleCloseReceipt: () => void;
};

export const useReceiptState = (
  input: UseReceiptStateInput
): UseReceiptStateOutput => {
  const { orderData, orderList } = input;
  const user = useSelector((state) => state.auth.user);
  const [isDisplayReceipt, setIsDisplayReceipt] = useState(false);

  // 検証は注文データが変わったときだけ行う。明細は特典や画像の到着で何度も入れ替わるため、
  // 明細と一緒に検証すると同じ不正データをそのたびにSentryへ送ってしまう
  const receiptOrderData = useMemo(() => {
    // 注文の読込前は検証しない。ダミーを渡すと不正データとして検知されてしまう
    if (orderData === null) return null;
    return parseReceiptOrderData(orderData);
  }, [orderData]);

  const receiptData = useMemo(() => {
    if (receiptOrderData === null) return null;
    return createReceiptData({
      orderData: receiptOrderData,
      orderList,
      userId: user.uid,
      userName: user.displayName ?? "",
    });
  }, [receiptOrderData, orderList, user.uid, user.displayName]);

  const handleShowReceipt = () => {
    setIsDisplayReceipt(true);
    scrollTop();
  };

  const handleCloseReceipt = () => {
    setIsDisplayReceipt(false);
    scrollTop();
  };

  return {
    isDisplayReceipt,
    isReceiptAvailable: receiptData !== null,
    receiptData,
    handleShowReceipt,
    handleCloseReceipt,
  };
};
